import React from "react";
import { Avatar, Box, Button, Grid, Paper, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomTooltip from "../../../../common/components/custom-tooltip";

import { Partner } from "./partner";
import { formatDate } from "../../../../utils/utils";

interface PartnerCardListProps {
  distributionType?: string;
  matchedPartners: Partner[];
  addedPartners: Partner[];
  onAdd: (partner: Partner) => void;
  onRemove: (partner: Partner) => void;
  viewRule?: boolean
  company_name?:string
}

const ListOfPartnersBasedOnRule: React.FC<PartnerCardListProps> = ({
  matchedPartners,
  addedPartners,
  onAdd,
  onRemove,
  distributionType,
  viewRule = false,
}) => {
  const formatStatus = (status: string | undefined | null) =>
    status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  
  const renderPartnerCard = (partner: Partner, action: "add" | "remove") => (
    <Grid item xs={12} sm={6} key={partner.id}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: '4px !important', sm: '16px' },   // p:3 = 24px, p:2 = 16px
    mt: { xs: '12px !important', sm: 0 },       // margin-top 16px on mobile only
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" }, 
          border: "1px solid #E0E0E0",
          borderRadius: 2,
          justifyContent: "space-between",
          height: { xs: "auto", sm: "70px" },
        }}
      >
        <Box sx={{
          display: "flex",
          alignItems: { xs: "center", sm: "flex-start" }, 
          width: { xs: "100%", sm: "auto" },
          ml: { xs: 2, sm: 0 },
        }}>
          <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
            {partner?.company_name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {partner?.company_name}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: partner?.status === "ACTIVE" ? "#2E7D32" : "red",
                }}
              >
                {formatStatus(partner?.status)}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "#6A6A6A" }}>
              {partner.email}
            </Typography>
            <Typography variant="caption" sx={{ color: "#6A6A6A" }}>
              Date Onboarded : {formatDate(partner.created_at) || "-"}
            </Typography>
          </Box>
        </Box>
        {action === "remove" && partner?._count?.assignedLeads === 0 ? (
          <CustomTooltip
            title="No leads assigned to the partner for this rule, you can remove the partner if you want"
            placement="bottom-start"
          >
            <span>
              <Button
                variant="text"
                color="error"
                onClick={() => onRemove(partner)}
                sx={{ fontWeight: 600 }}
              >
                {!viewRule && "Remove"}
              </Button>
            </span>
          </CustomTooltip>
        ) : (
          <Button
            variant="text"
            color={action === "add" ? "primary" : "error"}
            onClick={() =>
              action === "add" ? onAdd(partner) : onRemove(partner)
            }
            sx={{
              fontWeight: 600,
              width: { xs: "80%", sm: "auto" },
              mt: { xs: 2, sm: 0 }, 
              display: "block",
              mx: { xs: "auto", sm: 0 },
              border: {
                xs: `1px solid`,
                sm: "none",
              },
              borderColor: {
                xs: action === "add" ? "primary.main" : "error.main",
                sm: "transparent",
              },
            }}
            disabled={action === "remove" && partner?._count?.assignedLeads > 0}
          >
            {!viewRule && (action === "add" ? "Add" : "Remove")}
          </Button>
        )}
      </Paper>
    </Grid>
  );

  return (
    <Box className="list-of-partners">
      <>
        {
          distributionType  && <>  <Typography variant="body1" gutterBottom>
            Distribution : <strong>{distributionType}</strong>
          </Typography>
            <div className="algorithm-desc">
              <InfoOutlinedIcon sx={{ color: "#6A6A6A" }} />
              <div className="algorithm-desc-text">
                <Typography className="desc-type">{distributionType}</Typography>
                <Typography className="desc-content">
                  Distributes leads evenly and sequentially across all selected
                  entities. Each one receives leads in rotation, ensuring fair and
                  balanced allocation.
                </Typography>
              </div>
            </div></>
        }

      </>

      {/* Added Partners */}
      <Box
        sx={{
          boxShadow: "0px 0px 2px 0px #00000040",
          borderRadius: "8px",
          padding: "10px",
          marginTop: distributionType ? '20px' : '0px',
          marginBottom: "10px",
          background: "#F8F8F8",
        }}
      >
        <Box
          mb={3}
          sx={{
            boxShadow: "0px 0px 2px 0px #00000040",
            borderRadius: "8px",
            marginBottom: "10px",
            background: "#fff",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              textAlign: "center",
              fontSize: "16px",
              height: "54px",
              background: "#EBFEED",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            Partners Added
          </Typography>
          <Grid
            container
            spacing={2}
            sx={{
              padding: "10px",
              minHeight: "80px", // ensures space even when empty
              ...(addedPartners.length === 0 && {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }),
              // maxHeight: "310px",
              // overflowY:"auto"
            }}
          >
            {addedPartners.length > 0 ? (
              addedPartners.map((partner) =>
                renderPartnerCard(partner, "remove")
              )
            ) : (
              <Typography
                variant="body2"
                sx={{
                  ml: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Please add partner from the matched list.
              </Typography>
            )}
          </Grid>
        </Box>
        {/* Matched Partners */}
        {!viewRule && <Box
          mb={3}
          sx={{
            boxShadow: "0px 0px 2px 0px #00000040",
            borderRadius: "8px",
            background: "#fff",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              textAlign: "center",
              fontSize: "16px",
              height: "54px",
              background: "#F6F7F9",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            Partners Matched ({matchedPartners?.length})
          </Typography>
          <Grid
            container
            spacing={2}
            sx={{
              padding: "10px",
              minHeight: "80px", // ensures space even when empty
              ...(matchedPartners.length === 0 && {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }),
              // maxHeight: "310px",
              // overflowY: "auto"
            }}
          >
            {matchedPartners.length > 0 ? (
              matchedPartners.map((partner) =>
                renderPartnerCard(partner, "add")
              )
            ) : (
              <Typography variant="body2" sx={{ ml: 2, textAlign: "center" }}>
                No matched partners found.
              </Typography>
            )}
          </Grid>
        </Box>
        }
      </Box>
    </Box>
  );
};

export default ListOfPartnersBasedOnRule;
